import { useState, useEffect, useRef } from 'react' // 31 Aug 2025 - Added useEffect, 21 Sep 2025 - added useRef
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import useAuth from '@/hooks/useAuth'
import { useCreateNewUserActionMutation, useGetUserActionsQuery } from "@/features/userActions/userActionsApiSlice"
import { useGetActionTypesQuery } from "@/features/actionTypes/actionTypesApiSlice"
import { useGetUserTrailSessionExecutionsQuery } from "@/features/userTrailSessions/userTrailSessionExecutionsApiSlice"
import { useGetSessionsQuery } from "@/features/sessions/sessionsApiSlice"
import { useGetUsersQuery } from "@/features/users/usersApiSlice"

//07 Apr 2025
import { useGetUserMissionClientsQuery } from '@/features/userMissions/userMissionClientsApiSlice'
import { useGetUserMissionOrgsQuery } from '@/features/userMissions/userMissionOrgsApiSlice'
import { useGetUserMissionPartnersQuery } from '@/features/userMissions/userMissionPartnersApiSlice'
import { useGetUserMissionSelfsQuery } from '@/features/userMissions/userMissionSelfsApiSlice'
import { useGetUserMissionTeamsQuery } from '@/features/userMissions/userMissionTeamsApiSlice'

import { useGetUserVisionClientsQuery } from '@/features/userVisions/userVisionClientsApiSlice'
import { useGetUserVisionOrgsQuery } from '@/features/userVisions/userVisionOrgsApiSlice'
import { useGetUserVisionPartnersQuery } from '@/features/userVisions/userVisionPartnersApiSlice'
import { useGetUserVisionSelfsQuery } from '@/features/userVisions/userVisionSelfsApiSlice'
import { useGetUserVisionTeamsQuery } from '@/features/userVisions/userVisionTeamsApiSlice'

import { useGetUserPlanClientsQuery } from '@/features/userPlans/userPlanClientsApiSlice'
import { useGetUserPlanOrgsQuery } from '@/features/userPlans/userPlanOrgsApiSlice'
import { useGetUserPlanPartnersQuery } from '@/features/userPlans/userPlanPartnersApiSlice'
import { useGetUserPlanSelfsQuery } from '@/features/userPlans/userPlanSelfsApiSlice'
import { useGetUserPlanTeamsQuery } from '@/features/userPlans/userPlanTeamsApiSlice'

// 30 Aug 2025
import { useGetUserMissionGoalsQuery } from "@/features/userMissionGoals/userMissionGoalsApiSlice"

// 01 Sep 2025
import { convertLocalDateToUTCISO, formatUTCToLocalDateInput } from '@/utils/dateUtils'
import moment from 'moment';


const EtCreateUserActionLandingComp = ({ onClose = () => {}, refetchUserActions = () => {},defaultTrailId = '', defaultSessionId = '' }) => {

  // First Created: 06 Apr 2025  
  // Author: Atul Pandey  
  // Purpose: Create a standalone component to add a new user action from Trinity Web Landing  
  // Updated - 07 Apr 2025 - Added queries for User Visions, Missions and Plans
  // Updated - 17 May 2025 - Added defaultTrailId = '', defaultSessionId = '' to reuse the modal for user sessions
  // Updated - 18 Aug 2025 - refined for handling sharedVision and sharedMissionIds for incoming vision and mission data
  // Updated - 30 Aug 2025 - refined to add a goal to a new action
  // Updated - 30 Aug 2025 - refined to add recurring action with a pattern and start, end date
  // Updated - 30 Aug 2025 - refined to add a an action goal role - Enabler or Outcome
  // Updated - 31 Aug 2025 - refined to pre populate the actionType Name = "Mission" if the selected trail is "MyMissions"
  // Updated - 31 Aug 2025 - refined to pass weekdayOnly flag for "Daily" recurring actions
  // Updated - 31 Aug 2025 - added filteredPlanOptions.length >0 condition to display plans only when those exist
  // Updated - 01 Sep 2025 - added the date fix for local vs. UTC timezones for completeBy
  // Updated - 03 Sep 2025 - added logic for updating actionUoM
  // Updated - 04 Sep 2025 - added logic for updating targetValue
  // Updated - 07 Sep 2025 - added logic for assignedActions
  // Updated - 08 Sep 2025 - assignedUsers - assignmentType enum selection
  // Updated - 08 Sep 2025 - assignedUsers - restricted only for Sponsor users
  // Updated - 12 Sep 2025 - added goal period based action
  // Updated - 20 Sep 2025 - added useEffect to populate mission target and uom for Outcome actions directly connected to a mission
  // Updated - 21 Sep 2025 - added useRef for UoM and target reset 
  // Updated - 26 Sep 2025 - start date fix.
  // Updated - 01 Dec 2025 - time zone fix
  // Updated - 07 Dec 2025 - user time zone fix
  // Updated - 07 Dec 2025 - user time zone fix
  // Updated - 11 Dec 2025 — Prevent recurrenceStart/EndDate from exceeding completeBy
  // Updated - 11 Dec 2025 — Assigned to users based on user's choice


  // console.log('insite EtCreateUserActionLandingComp...') // 25 Aug 2025 - Debug

  // 1️⃣ Auth and Local User Info
  const { username } = useAuth()
  // console.log('username', username)

  // 2️⃣ State Variables for Form Inputs
  // const [selectedTrailId, setSelectedTrailId] = useState('')
  // const [selectedSessionId, setSelectedSessionId] = useState('')
  const [selectedTrailId, setSelectedTrailId] = useState(defaultTrailId)          // ✅ 17 May 2025
  const [selectedSessionId, setSelectedSessionId] = useState(defaultSessionId)    // ✅ 17 May 2025
  const [actionName, setActionName] = useState('')
  const [actionDesc, setActionDesc] = useState('')
  const [selectedActionTypeId, setSelectedActionTypeId] = useState('')
  const [completionDate, setCompletionDate] = useState('')

  // 07 Apr 2025 - Additional states for MyMissions trail support
  const [selectedFocus, setSelectedFocus] = useState('');
  const [selectedVisionId, setSelectedVisionId] = useState('');
  const [selectedMissionId, setSelectedMissionId] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');

  // 30 Aug 2025 - State variables for Goals
  const [selectedGoalId, setSelectedGoalId] = useState('')
  // const [selectedSharedGoalId, setSelectedSharedGoalId] = useState('')
  const [actionContributionValue, setActionContributionValue] = useState('')

  // 30 Aug 2025 - State variables for Recurring Actions
  const [actionRecurrencePattern, setActionRecurrencePattern] = useState('OneTime')
  const [recurrenceStartDate, setRecurrenceStartDate] = useState('')
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('')

  // 30 Aug 2025 - State Variable for Action Role for the Goal - Enabler or Outcome
  const [actionGoalRole, setActionGoalRole] = useState('Enabler') // default
  const [weekdayOnly, setWeekdayOnly] = useState(false) // 31 Aug 2025

  const [actionUoM, setActionUoM] = useState("")     // 03 Sep 2025
  const [targetValue, setTargetValue] = useState(0) // 04 Sep 2025

  // 07 Sep 2025 - Assigned Users
  const [assignedUsers, setAssignedUsers] = useState([]); 

  // 08 Sep 2025 - AssignmentType
  const [assignmentType, setAssignmentType] = useState("Collective"); 

  // 12 Sep 2025 - Goal Period
  // const [selectedGoalBreakdownId, setSelectedGoalBreakdownId] = useState(""); // 23 Sep 2025 - Issue
  const [selectedGoalBreakdownId, setSelectedGoalBreakdownId] = useState(null); // 23 Sep 2025 - Fix

  // 11 Dec 2025 - NEW toggle for showing/hiding assignment section
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false);



  // 3️⃣ API Hooks - Queries & Mutations
  const { data: actionTypesData } = useGetActionTypesQuery() 
  const { data: userTrailSessionsData } = useGetUserTrailSessionExecutionsQuery() 
  const { data: sessionsData } = useGetSessionsQuery() 
  const { data: usersData } = useGetUsersQuery();
  const [createNewUserAction] = useCreateNewUserActionMutation()

  const { data: userMissionClientsData } = useGetUserMissionClientsQuery()    // 07 Apr 2025
  const { data: userMissionOrgsData } = useGetUserMissionOrgsQuery()          // 07 Apr 2025
  const { data: userMissionPartnersData } = useGetUserMissionPartnersQuery()  // 07 Apr 2025
  const { data: userMissionSelfsData } = useGetUserMissionSelfsQuery()        // 07 Apr 2025
  const { data: userMissionTeamsData } = useGetUserMissionTeamsQuery()        // 07 Apr 2025
  
  const { data: userVisionClientsData } = useGetUserVisionClientsQuery()      // 07 Apr 2025
  const { data: userVisionOrgsData } = useGetUserVisionOrgsQuery()            // 07 Apr 2025
  const { data: userVisionPartnersData } = useGetUserVisionPartnersQuery()    // 07 Apr 2025
  const { data: userVisionSelfsData } = useGetUserVisionSelfsQuery()          // 07 Apr 2025
  const { data: userVisionTeamsData } = useGetUserVisionTeamsQuery()          // 07 Apr 2025

  const { data: userPlanClientsData } = useGetUserPlanClientsQuery()          // 07 Apr 2025
  const { data: userPlanOrgsData } = useGetUserPlanOrgsQuery()                // 07 Apr 2025
  const { data: userPlanPartnersData } = useGetUserPlanPartnersQuery()        // 07 Apr 2025
  const { data: userPlanSelfsData } = useGetUserPlanSelfsQuery()              // 07 Apr 2025
  const { data: userPlanTeamsData } = useGetUserPlanTeamsQuery()              // 07 Apr 2025

  const { data: userMissionGoalsData } = useGetUserMissionGoalsQuery()        // 30 Aug 2025


  const extractFilteredUserEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        (item) => item?.username === username
        );
  };

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
  };

  const extractFilteredEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id])
    ?.filter(
        // (item) => item.userId?._id === userId && item?.userOrgId === userOrgId
        (item) => item.userId?._id === userId
        );
  };

  const userInfo = extractFilteredUserEntitiesFromAdapter(usersData)
  const users = extractEntitiesFromAdapter(usersData)
  
  // 4️⃣ Extract userId and userOrgId from current user
  const userId = userInfo[0]?._id || null
  const userOrgId = userInfo[0]?.userOrgId?._id || null
  const userRoles = userInfo[0]?.roles || []      // 08 Sep 2025

  // console.log('userId', userId)
  // console.log('userOrgId', userOrgId)
  // console.log('users', users)
  // console.log('userInfo', userInfo)
  // console.log('userRoles', userRoles)

  const actionTypes = extractEntitiesFromAdapter(actionTypesData);
  const sessions = extractEntitiesFromAdapter(sessionsData);
  const userTrailSessions = extractFilteredEntitiesFromAdapter(userTrailSessionsData)

  const userMissionClients = extractEntitiesFromAdapter(userMissionClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userMissionOrgs = extractEntitiesFromAdapter(userMissionOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userMissionPartners = extractEntitiesFromAdapter(userMissionPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userMissionSelfs = extractEntitiesFromAdapter(userMissionSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userMissionTeams = extractEntitiesFromAdapter(userMissionTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  
  const userVisionClients = extractEntitiesFromAdapter(userVisionClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userVisionOrgs = extractEntitiesFromAdapter(userVisionOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userVisionPartners = extractEntitiesFromAdapter(userVisionPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userVisionSelfs = extractEntitiesFromAdapter(userVisionSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userVisionTeams = extractEntitiesFromAdapter(userVisionTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025

  const userPlanClients = extractEntitiesFromAdapter(userPlanClientsData)?.filter(item => item.userId?._id === userId)     //07 Apr 2025
  const userPlanOrgs = extractEntitiesFromAdapter(userPlanOrgsData)?.filter(item => item.userId?._id === userId)           //07 Apr 2025
  const userPlanPartners = extractEntitiesFromAdapter(userPlanPartnersData)?.filter(item => item.userId?._id === userId)   //07 Apr 2025
  const userPlanSelfs = extractEntitiesFromAdapter(userPlanSelfsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025
  const userPlanTeams = extractEntitiesFromAdapter(userPlanTeamsData)?.filter(item => item.userId?._id === userId)         //07 Apr 2025

  const userMissionGoals = extractEntitiesFromAdapter(userMissionGoalsData)?.filter(goal => goal.userId?._id === userId)       //30 Aug 2025


  // console.log('userVisionClients', userVisionClients) // Debug - 20 Sep 2025
  // console.log('userMissionClients', userMissionClients) // Debug - 20 Sep 2025
  // console.log('userMissionGoals', userMissionGoals) // Debug - 20 Sep 2025
  
  // console.log('userVisionSelfs', userVisionSelfs) // Debug - 20 Sep 2025
  // console.log('userMissionSelfs', userMissionSelfs) // Debug - 20 Sep 2025
  
  // 30 Aug 2025 - Extract Goals connected to the selected MissionId
  const filteredGoalOptions = userMissionGoals?.filter(
    (goal) => 
      goal.missionId?._id === selectedMissionId || 
      goal.missionId === selectedMissionId
  ) || []

  // console.log('user vision selfs', userVisionSelfs)
  // console.log('user mission selfs', userMissionSelfs)
  // console.log('user plan selfs', userPlanSelfs)

  // console.log('user vision clients', userVisionClients)
  // console.log('userMissionClientsData', userMissionClientsData)
  // console.log('user mission clients', userMissionClients)
  // console.log('user plan clients', userPlanClients)
  
  // console.log('user mission goals', userMissionGoals)
  // console.log('filtered mission goals', filteredGoalOptions)


  // 07 Apr 2025 - Helper functions for filtered dropdown options

const filteredVisionOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userVisionSelfs || []
      case 'Client': return userVisionClients || []
      case 'Org': return userVisionOrgs || []
      case 'Team': return userVisionTeams || []
      case 'Partner': return userVisionPartners || []
      default: return []
    }
  })()
  
  const filteredMissionOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userMissionSelfs?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Client': return userMissionClients?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Org': return userMissionOrgs?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Team': return userMissionTeams?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      case 'Partner': return userMissionPartners?.filter(m => m.visionId?._id === selectedVisionId || m.visionId === selectedVisionId) || []
      default: return []
    }
  })()
  
  const filteredPlanOptions = (() => {
    switch (selectedFocus) {
      case 'Self': return userPlanSelfs?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Client': return userPlanClients?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Org': return userPlanOrgs?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Team': return userPlanTeams?.filter(p => p.missionId?._id === selectedMissionId) || []
      case 'Partner': return userPlanPartners?.filter(p => p.missionId?._id === selectedMissionId) || []
      default: return []
    }
  })()

  // console.log('selectedVisionId', selectedVisionId)
  // console.log('selectedMissionId', selectedMissionId)
  // console.log('filteredVisionOptions', filteredVisionOptions)
  // console.log('filteredMissionOptions', filteredMissionOptions)
  // console.log('filteredPlanOptions', filteredPlanOptions)

  
  
  // 5️⃣ Group Sessions by Trail
  const trails = userTrailSessions?.reduce((acc, session) => {
    const trailId = session.trailId?._id;
    if (!acc[trailId]) {
      acc[trailId] = {
        trailId,
        trailName: session.trailId?.trailName || "Unnamed Trail",
        sessions: [],
      };
    }
    acc[trailId].sessions.push({
      sessionId: session?.sessionId?._id,
      sessionName: session.sessionId?.sessionName || "Unnamed Session",
    });
    return acc;
  }, {});
  // console.log('trails', trails)

  // 6️⃣ Convert trails object to array of options
  const trailOptions = Object.values(trails || {});
  // console.log('trailOptions', trailOptions)

  // 31 Aug 2025 - Prepopulate actionType for "MyMissions"
  useEffect(() => {
    const selectedTrail = trailOptions.find(t => t.trailId === selectedTrailId)
    const trailName = selectedTrail?.trailName

    if (trailName === "MyMissions" && actionTypes?.length > 0) {
      const missionActionType = actionTypes.find(a => a.actionTypeName === "Mission")
      if (missionActionType) {
        setSelectedActionTypeId(missionActionType._id)
      }
    }
  // }, [selectedTrailId, actionTypes])
  }, []) // 14 Sep 2025 - to make it run only once.

  // 12 Sep 2025 - Goal Period Breakdown
  const selectedGoalObj = filteredGoalOptions?.find(goal => goal._id === selectedGoalId);

  const goalBreakdownList =
  selectedFocus === "Client" || selectedFocus === "Org" || selectedFocus === "Partner"
    ? selectedGoalObj?.sharedGoalId?.goalBreakdown || []
    : selectedGoalObj?.goalBreakdown || [];


  // console.log('selectedGoalObj', selectedGoalObj)
  // console.log('goalBreakdownList', goalBreakdownList)

  // 21 Sep 2025 - useRef for target and uom reset
  const prevGoalRoleRef = useRef(); // ⬅️ Outside the useEffect

  // 12 Sep 2025 - UseEffect for setting goal or goal period level values for actionUoM and targetValue for "Outcome" actions
  useEffect(() => {

    const prevGoalRole = prevGoalRoleRef.current; // 21 Sep 2025

    // if (actionGoalRole === "Enabler") {
    //   setTargetValue(0);
    //   setActionUoM("");
    //   return; // Exit early
    // }

     // ✅ 21 Sep 2025 - Only reset if switching *from Outcome to Enabler*
    if (actionGoalRole === "Enabler" && prevGoalRole === "Outcome") {
      setTargetValue(0);
      setActionUoM("");
      prevGoalRoleRef.current = actionGoalRole; // ⬅️ Update for next run
      return;
    }

    prevGoalRoleRef.current = actionGoalRole; // 21 Sep 2025

    // if (actionGoalRole !== "Outcome" || !selectedGoalObj || !selectedFocus) return;
    if (actionGoalRole !== "Outcome" || !selectedFocus) return;

    // 🧭 Determine whether to use sharedGoalId or regular goal fields
    const isSharedFocus = ["Client", "Org", "Partner"].includes(selectedFocus);


    // ✅ 20 Sep 2025 - If no goal but mission selected, apply mission logic
  if (!selectedGoalId && selectedMissionId) {
    
    const mission = filteredMissionOptions.find((m) => m?._id === selectedMissionId);

    const missionObj = isSharedFocus ? mission?.sharedMissionId : mission;
    
    if (missionObj) {
      const missionTarget = missionObj?.mission1YrTargetValue;
      const missionUoM = missionObj?.missionUoMType?.missionMetricUoMTypeName;

      if (missionTarget !== undefined && missionTarget !== null) {
        setTargetValue(missionTarget);
      }

      if (missionUoM) {
        setActionUoM(missionUoM);
      }
    }
  }

   // ✅ If Goal is selected, apply goal logic
    if (selectedGoalObj) {
    // 🔶 UoM: set from shared or regular goal
    const goalUoM = isSharedFocus
      ? selectedGoalObj.sharedGoalId?.goalUoM || ""
      : selectedGoalObj?.goalUoM || "";

    setActionUoM(goalUoM);

    // 🔹 Goal Breakdown (period) check
    const breakdownArray = isSharedFocus
      ? selectedGoalObj.sharedGoalId?.goalBreakdown || []
      : selectedGoalObj.goalBreakdown || [];

    if (selectedGoalBreakdownId) {
      const period = breakdownArray.find(
        (b) => b._id === selectedGoalBreakdownId
      );
      if (period) {
        setTargetValue(period?.targetValue || 0);
        return; // ✅ Stop here if goal period is found
      }
    }

    // 🔸 Fallback to goal-level target
    const goalTargetValue = isSharedFocus
      ? selectedGoalObj.sharedGoalId?.goalTargetValue || 0
      : selectedGoalObj.goalTargetValue || 0;

    setTargetValue(goalTargetValue);
    return;
  }

  }, [
    actionGoalRole, 
    selectedGoalObj, 
    selectedGoalBreakdownId, 
    selectedFocus,
    selectedGoalId,
    selectedMissionId,
    filteredMissionOptions,
   ]);


  // 7️⃣ Form Submit Handler
  const handleSubmit = async () => {
    // Validation
    if (!selectedTrailId || !selectedSessionId || !selectedActionTypeId || !actionName || !completionDate) {
      alert("Please fill in all fields: Trail, Session,Action Type, Action Name and Completion Date")
      return
    }

    // 01 Sep 2025
    const utcCompletionDate = convertLocalDateToUTCISO(completionDate)

    // ✅ 30 Aug 2025 - Recurrence validation
    if (actionRecurrencePattern !== "OneTime") {
      if (!recurrenceStartDate || !recurrenceEndDate) {
        alert("Please provide both start and end dates for recurring actions.")
        return
      }

      // const today = new Date()
      // today.setHours(0, 0, 0, 0) // Normalize to start of day
      // const start = new Date(recurrenceStartDate)
      // const end = new Date(recurrenceEndDate)

      // 26 Sep 2025
      // const today = moment().startOf('day'); // Local start of today
      // const start = moment(recurrenceStartDate).startOf('day'); // Parsed local date from input
      // const end = moment(recurrenceEndDate).startOf('day');

      // 07 Dec 2025
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const today = moment().tz(userTimeZone).startOf('day');
      const start = moment.tz(recurrenceStartDate, 'YYYY-MM-DD', userTimeZone).startOf('day');
      const end = moment.tz(recurrenceEndDate, 'YYYY-MM-DD', userTimeZone).startOf('day');


      console.log('today', today)
      console.log('start', start)

      if (start < today) {
        alert("Recurrence start date cannot be in the past.")
        return
      }

      if (end < today) {
        alert("Recurrence end date cannot be in the past.")
        return
      }

      if (start > end) {
        alert("Recurrence start date cannot be after end date.")
        return
      }
    }

    // 🚫 11 Dec 2025 – Prevent recurrence dates from exceeding action's completeBy
    if (actionRecurrencePattern !== "OneTime") {

      if (recurrenceStartDate && completionDate) {
        const start = moment(recurrenceStartDate).startOf("day");
        const completeBy = moment(completionDate).startOf("day");

        if (start.isAfter(completeBy)) {
          alert("Recurrence Start Date cannot be after the Action's Complete By date.");
          return;
        }
      }

      if (recurrenceEndDate && completionDate) {
        const end = moment(recurrenceEndDate).startOf("day");
        const completeBy = moment(completionDate).startOf("day");

        if (end.isAfter(completeBy)) {
          alert("Recurrence End Date cannot be after the Action's Complete By date.");
          return;
        }
      }
    }


    const selectedTrailName = trailOptions.find(t => t.trailId === selectedTrailId)?.trailName // 07 Apr 2025

    // ⛔ Ensure vision is at least selected if trail is MyMissions
    if (
      selectedTrailName === "MyMissions" &&
      selectedFocus &&
      !selectedVisionId &&
      !selectedMissionId &&
      !selectedPlanId
    ) {
      alert("Please select at least a Vision before submitting an action in the MyMissions trail.");
      return;
    }

    // 07 Dec 2025
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;


    const newAction = {
      userId,
      userOrgId,
      trailId: selectedTrailId,
      sessionId: selectedSessionId,
      actionTypeId: selectedActionTypeId,
      userActionName: actionName,
      userActionDesc: actionDesc,
      // completeBy: completionDate,
      completeBy: utcCompletionDate, // 01 Sep 2025
      selectedFocus: selectedFocus, // 08 Apr 2025
      actionGoalRole: actionGoalRole, // 30 Aug 2025
      
      actionUoM: actionUoM, // 01 Dec 2025
      targetValue: targetValue, // 01 Dec 2025

      // ⭐️ 07 Dec 2025 - NEW (CRITICAL)
      userTimeZone: userTimeZone,
    }

    // ✅ 07 Sep 2025 - Assigned Users Assignment
    // if (assignedUsers.length > 0) {
    //   newAction.assignedUsers = assignedUsers;
    // }
    if (assignedUsers.length > 0) {
      newAction.assignedUsers = assignedUsers.map((id) => ({
        userId: id,
        // assignmentType: 'Collective',
        assignmentType: assignmentType, // 08 Sep 2025
      }));
    }

    // ✅ 30 Aug 2025 - Connect to a Goal if selected
    if (selectedGoalId) {
      const matchingGoal = userMissionGoals?.find(goal => goal._id === selectedGoalId)

      if (matchingGoal) {
        newAction.connectedMissionGoals = [
          {
            connectedMissionGoalId: selectedGoalId,
            connectedSharedGoalId: matchingGoal.sharedGoalId?._id || undefined,
            connectedGoalBreakdownId: selectedGoalBreakdownId, // 12 Sep 2025
            // actionContributionValue: undefined // future use
          }
        ]
      }
    }

    // ✅ 30 Aug 2025 - Add recurrence pattern and dates
    newAction.actionRecurrencePattern = actionRecurrencePattern

    if (actionRecurrencePattern !== "OneTime") {
      // 01 Dec 2025
      // newAction.recurrenceStartDate = recurrenceStartDate
      // newAction.recurrenceEndDate = recurrenceEndDate
      newAction.recurrenceStartDate = convertLocalDateToUTCISO(recurrenceStartDate)
      newAction.recurrenceEndDate = convertLocalDateToUTCISO(recurrenceEndDate)
      // 31 Aug 2025
      if (actionRecurrencePattern === "Daily") {
        newAction.weekdayOnly = weekdayOnly
      }
    }

    // 07 Apr 2025 - Added MyMissions-specific fields if trail is MyMissions
    if (selectedTrailName === "MyMissions" && selectedFocus) {

        if (selectedVisionId) {
          newAction[`actionVision${selectedFocus}Ids`] = [{ visionId: selectedVisionId }]
        }

        if (selectedMissionId) {
          newAction[`actionMission${selectedFocus}Ids`] = [{ missionId: selectedMissionId }]
        }

        if (selectedPlanId) {
          newAction[`actionPlan${selectedFocus}Ids`] = [{ planId: selectedPlanId }]
        }
      }

    try {
      await createNewUserAction(newAction).unwrap()
      if (refetchUserActions) refetchUserActions()
      onClose()
    } catch (err) {
      console.error("Error creating action:", err)
      alert("Failed to create action")
    }
  }

  // 8- Handle Cancel 
  const handleCancel = () => {
    if (onClose) onClose()
  }


  return (
    <div className="bg-gray-100 m-6 p-4 rounded shadow-lg font-poppins">
      <p className="text-sm font-bold text-orange-600 mb-4 pb-4 border-white border-b">Create a new Action</p>

      {/* 8️⃣ Full JSX Form UI */}
        <form className="space-y-3 text-sm text-gray-700 font-poppins" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        
        {/* Trail Selection */}
        <p className="font-bold text-orange-800 mt-2 mx-1 px-1">Trail</p>
        <select
            value={selectedTrailId}
            onChange={(e) => {
            setSelectedTrailId(e.target.value)
            setSelectedSessionId("")
            }}
            disabled={!!defaultTrailId} // ✅ 17 May 2025 Disable if default provided
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Trail</option>
            {trailOptions?.map(trail => (
            <option key={trail.trailId} value={trail.trailId}>
                {trail.trailName}
            </option>
            ))}
        </select>

        {/* 07 Apr 2025 - Only show Focus dropdown if trail is MyMissions */}
        
        {selectedTrailId && trailOptions.find(t => t.trailId === selectedTrailId)?.trailName === "MyMissions" && (
        <>
            
            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Focus Area <span className='font-normal text-gray-700 italic text-xs'>(e.g., Self, Client, Org...)</span> </p>
            {/* a) Focus Selector */}
            <select
            value={selectedFocus}
            onChange={(e) => {
                setSelectedFocus(e.target.value)
                setSelectedVisionId('')
                setSelectedMissionId('')
                setSelectedPlanId('')
            }}
            className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
            <option value="">Select Focus (Self, Client...)</option>
            <option value="Self">Self</option>
            <option value="Client">Client</option>
            <option value="Org">Org</option>
            <option value="Team">Team</option>
            <option value="Partner">Partner</option>
            </select>

            {/* b) Vision Dropdown */}
           
            {selectedFocus && (
            <>
              <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Vision</p>
              <select
                  value={selectedVisionId}
                  onChange={(e) => {
                  setSelectedVisionId(e.target.value)
                  setSelectedMissionId('')
                  setSelectedPlanId('')
                  }}
                  className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
              >
                  <option value="">Select Vision</option>
                  {/* {filteredVisionOptions?.map((vision) => (
                  <option key={vision._id} value={vision._id}>
                      {vision.visionName}
                  </option>
                  ))} */}

                  {/* 18 Aug 2025 */}
                  {filteredVisionOptions?.map((vision) => {
                    const displayName = vision?.sharedVisionId
                      ? vision?.sharedVisionId?.visionName
                      : vision?.visionName;

                    return (
                      <option key={vision._id} value={vision._id}>
                        {displayName}
                      </option>
                    );
                  })}
              </select>
            </>
            )}

            {/* c) Mission Dropdown */}
            
            {selectedVisionId && (
            <>
              <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Mission</p>
              <select
                  value={selectedMissionId}
                  onChange={(e) => {
                  setSelectedMissionId(e.target.value)
                  setSelectedPlanId('')
                  }}
                  className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
              >
                  <option value="">Select Mission</option>
                  {/* {filteredMissionOptions?.map((mission) => (
                  <option key={mission._id} value={mission._id}>
                      {mission.missionName}
                  </option>
                  ))} */}

                  {/* 18 Aug 2025 */}
                  {filteredMissionOptions?.map((mission) => {
                    const displayName = mission?.sharedMissionId
                      ? mission?.sharedMissionId?.missionName
                      : mission?.missionName;

                    return (
                      <option key={mission._id} value={mission._id}>
                        {displayName}
                      </option>
                    );
                  })}
              </select>
            </>
            )}

            {/* d) Plan Dropdown */}
            
            {selectedMissionId && filteredPlanOptions?.length >0 && ( //31 Aug 2025 - added .length condition to display plans only when those exist
              <>
                <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Plan</p>
                <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
                >
                    <option value="">Select Plan</option>
                    {filteredPlanOptions?.map((plan) => (
                    <option key={plan._id} value={plan._id}>
                        {plan.planName}
                    </option>
                    ))}
                </select>
              </>
            )}
        </>
        )}

        {/* 30 Aug 2025 - Select Mission Goal */}        
        {selectedMissionId && (
          <>
            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Goal</p>
            <select
              value={selectedGoalId}
              onChange={(e) => setSelectedGoalId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            >
              <option value="">Select Goal (optional)</option>
              {filteredGoalOptions?.map((goal) => {
                const displayName = goal?.sharedGoalId
                  ? goal.sharedGoalId.goalName
                  : goal.goalName;

                return (
                  <option key={goal._id} value={goal._id}>
                    {displayName}
                  </option>
                );
              })}
            </select>
          </>

        )}

        {/* 12 Sep 2025 - Select Goal Period if Goal is selected */}
        {goalBreakdownList.length > 0 && (
          <div className="my-2">
            {/* 1️⃣ Label */}
            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Select Goal Period</p>

            {/* 2️⃣ Button-style chips */}
            <div className="flex flex-wrap gap-2 bg-white rounded m-1 p-2">
              {goalBreakdownList.map((period) => {
                const isSelected = selectedGoalBreakdownId === period._id;
                const displayLabel = period.periodLabel || `${period.periodType} – ${period.period}`;

                return (
                  <button
                    key={period._id}
                    type="button"
                    onClick={() =>
                      setSelectedGoalBreakdownId(isSelected ? "" : period._id) // toggle off if clicked again
                    }
                    className={`px-3 py-1 rounded-full text-sm border font-medium transition
                      ${
                        isSelected
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-orange-100"
                      }`}
                  >
                    {displayLabel}
                  </button>
                );
              })}
            </div>
          </div>
        )}


        {/* 🔸 30 Aug 2025 – Toggle for Goal Role */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action's Goal Role</p>
        <div className="flex space-x-2 mx-1 px-1">
          {["Enabler", "Outcome"].map((role) => (
            <button
              key={role}
              type="button"
              className={`px-3 py-1 rounded-full text-sm font-semibold border transition 
                ${actionGoalRole === role
                  ? "bg-orange-500 text-white border-orange-600"
                  : "bg-white text-gray-800 border-gray-300 hover:bg-orange-100"}`}
              onClick={() => setActionGoalRole(role)}
            >
              {role}
            </button>
          ))}
        </div>


        {/* 30 Aug 2025 - Select Contribution Value - optional */}
        {/* <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action Contribution</p>
        {selectedGoalId && (
          <input
            type="number"
            value={actionContributionValue}
            onChange={(e) => setActionContributionValue(e.target.value)}
            placeholder="Contribution Value (optional)"
            className="w-full border border-gray-300 rounded-lg p-3 mb-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
        )} */}

        {/* Session Selection */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Session</p>
        <select
            value={selectedSessionId}
            onChange={(e) => setSelectedSessionId(e.target.value)}
            disabled={!!defaultSessionId || !selectedTrailId}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Session</option>
            {selectedTrailId &&
            trails[selectedTrailId]?.sessions.map(session => (
                <option key={session.sessionId} value={session.sessionId}>
                {session.sessionName}
                </option>
            ))}
        </select>

        {/* Action Type Selection */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action Type</p>
        <select
            value={selectedActionTypeId}
            onChange={(e) => setSelectedActionTypeId(e.target.value)}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        >
            <option value="">Select Action Type</option>
            {actionTypesData?.ids.map(id => {
            const actionType = actionTypesData.entities[id]
            // console.log('actionType', actionType)
            return (
                <option key={id} value={id}>
                {actionType?.actionTypeName}
                </option>
            )
            })}
        </select>

        {userRoles.includes("OrgSponsor") && (
          <>    

            {/* 11 Dec 2025 — NEW: Toggle checkbox to show/hide assignment UI */}
            <div className="flex items-center gap-2 mt-2 pt-2 mx-1 px-1">
              <input
                type="checkbox"
                checked={showAssigneeSelector}
                onChange={() => setShowAssigneeSelector(!showAssigneeSelector)}
              />
              <label className="font-bold text-orange-800">Assign this Action?</label>
            </div>

            {/* 11 Dec 2025 - Only show when toggle is ON */}
            {showAssigneeSelector && (
              <div className='bg-white rounded m-2 p-3 border'>
              
                {/* 07 Sep 2025 - Assign Users */}
                <div>
                  {/* 1️⃣ Label */}
                  <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Assign To</p>

                  {/* 2️⃣ Badge-style chip selector */}
                  <div className="flex flex-wrap gap-2 mb-4 bg-white rounded m-1 p-2">
                    {usersData?.ids
                      .map((id) => usersData.entities[id])
                      .filter((user) => user._id !== userId && user.userOrgId?._id === userOrgId)
                      .map((user) => {
                        const isSelected = assignedUsers.includes(user._id);
                        return (
                          <button
                            key={user._id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setAssignedUsers(prev => prev.filter(id => id !== user._id));
                              } else {
                                setAssignedUsers(prev => [...prev, user._id]);
                              }
                            }}
                            className={`px-3 py-1 rounded-full text-sm border font-medium transition 
                              ${
                                isSelected
                                  ? "bg-orange-500 text-white border-orange-500"
                                  : "bg-white text-gray-700 border-gray-300 hover:bg-orange-100"
                              }`}
                          >
                            {user.name} {user.userOrgRole ? `- ${user.userOrgRole.orgRoleName}` : ''}
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* 🔸 08 Sep 2025 – Toggle for Assignment Type */}
                <p className="font-bold text-orange-800 mt-2 pt-2 m-1 p-1">Assignment Type</p>
                <div className="flex space-x-2 mx-1 px-1">
                  {["Delegated", "Collective"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`px-3 py-1 rounded-full text-sm font-semibold border transition 
                        ${assignmentType === type
                          ? "bg-orange-500 text-white border-orange-600"
                          : "bg-white text-gray-800 border-gray-300 hover:bg-orange-100"}`}
                      onClick={() => setAssignmentType(type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              
              </div>
            )}


            
          </>
        )}


        {/* Action Name Input */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action Name</p>
        <input
            type="text"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Enter Action Name"
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

        {/* Completion Date */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action Complete By</p>
        <input
            type="date"
            // value={formatUTCToLocalDateInput(completionDate)}
            value={completionDate}
            onChange={(e) => setCompletionDate(e.target.value)}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

        {/* 12 Sep 2025 - targetValue and actionUoM */}
        <div className="flex justify-between items-center mb-2">
            <div id="targetValue" className="mt-2 pt-2 w-full mr-1 pr-1">
              <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Target Value</p>
              <input
                type="number"
                min="0"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="actionUoM" className="mt-2 pt-2 w-full pr-1 mr-1">
              <p className="font-bold text-orange-800 mb-2 mx-1 px-1">Unit of Measure (UoM)</p>
              <input
                type="text"
                name="actionUoM"
                placeholder="e.g., messages sent, leads contacted"
                value={actionUoM}
                onChange={(e) => setActionUoM(e.target.value)}
                className="w-full rounded p-2 m-1"
              />
            </div>
          </div>

        {/* 30 Aug 2025 - Recurrence Block */}
        {/* Label */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence Pattern</p>
        {/* Toggle Buttons */}
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            "OneTime", "Daily", "Weekly", "BiWeekly",
            "Monthly", "Quarterly", "SemiAnnually", "Annually"
          ].map((pattern) => (
            <button
              key={pattern}
              type="button"
              className={`rounded-full px-4 py-1 text-sm border ${
                actionRecurrencePattern === pattern
                  ? "bg-orange-500 text-white font-bold"
                  : "bg-white text-gray-600 border-gray-300"
              }`}
              onClick={() => setActionRecurrencePattern(pattern)}
            >
              {pattern === "OneTime" ? "One-Time" : pattern}
            </button>
          ))}
        </div>

        {/* 30 Aug 2025 - Recurrence Window for Recurring Actions */}
        {actionRecurrencePattern !== "OneTime" && (
          <div>
            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence Start Date</p>
            <input
              type="date"
              value={recurrenceStartDate}
              onChange={(e) => setRecurrenceStartDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />

            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Recurrence End Date</p>
            <input
              type="date"
              value={recurrenceEndDate}
              onChange={(e) => setRecurrenceEndDate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
          </div>
        )}

        {/* 31 Aug 2025 - weekDayOnly flag for the backend */}
        {actionRecurrencePattern === "Daily" && (
          <>
            <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Weekdays Only?</p>
            <div className="flex items-center space-x-4 mx-2 mb-2">
              <button
                type="button"
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                  weekdayOnly ? 'bg-orange-500 text-white' : 'bg-white text-gray-800'
                }`}
                onClick={() => setWeekdayOnly(true)}
              >
                Yes
              </button>
              <button
                type="button"
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${
                  !weekdayOnly ? 'bg-orange-500 text-white' : 'bg-white text-gray-800'
                }`}
                onClick={() => setWeekdayOnly(false)}
              >
                No
              </button>
            </div>
          </>
        )}


        {/* Action Description */}
        <p className="font-bold text-orange-800 mt-2 pt-2 mx-1 px-1">Action Description</p>
        <textarea
            value={actionDesc}
            onChange={(e) => setActionDesc(e.target.value)}
            placeholder="Enter Action Description"
            rows={4}
            className="w-full rounded p-2 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-orange-500"
        />

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
                <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
                />
                <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
                />
            </div>
        </form>

    </div>
  )
}

export default EtCreateUserActionLandingComp